import { Model } from 'survey-core';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { DOMAIN, SESSION_ID_DATA_LAYER_KEY } from '@/utils/constants';

export function buildUnifiedSearchParams(...paramsSources: Map<string, string>[]) {
  return paramsSources.reduce(
    (result, params) => new Map([...result, ...params]),
    new Map<string, string>(),
  );
}

export function buildCookieSearchParamsString() {
  const ovivaCookieApi = Cookies.withAttributes({
    path: '/',
    domain: '.' + DOMAIN,
  });
  const allowedCookiePrefixes = ['oviva'];
  const cookies = ovivaCookieApi.get();
  const filteredCookies = Object.entries(cookies)
    .filter(([_, value]) => Boolean(value))
    .filter(([key]) => allowedCookiePrefixes.some((prefix) => key.startsWith(prefix)));

  return new Map(filteredCookies);
}

const SURVEY_VALUES_COOKIE_KEY = 'survey_values';
export function getSurveyValuesFromCookie(): Map<string, string> {
  const surveyValuesCookie = Cookies.get(SURVEY_VALUES_COOKIE_KEY);

  if (!surveyValuesCookie) {
    return new Map<string, string>();
  }
  return new Map(Object.entries(JSON.parse(surveyValuesCookie)));
}

export const setSurveyValuesCookie = (values: Record<string, string | number | null>) => {
  const expiresAt = new Date();
  expiresAt.setMinutes(expiresAt.getMinutes() + 30);

  const existingValues = getSurveyValuesFromCookie();
  const merged = { ...Object.fromEntries(existingValues), ...values };

  Cookies.set(SURVEY_VALUES_COOKIE_KEY, JSON.stringify(merged), {
    expires: expiresAt,
    domain: '.' + DOMAIN,
    path: '/',
    secure: true,
  });
};

export function initSessionIdentifier() {
  const sessionUUID: string = uuidv4();
  const sessionIdPair = new Map(Object.entries({ [SESSION_ID_DATA_LAYER_KEY]: sessionUUID }));

  if (window && window.dataLayer) {
    window.dataLayer.push(Object.fromEntries(sessionIdPair));
  }

  return sessionIdPair;
}

export function setExperimentVariable(survey: Model) {
  const experiment = Cookies.get('oviva_experiment');
  if (experiment) {
    survey.setVariable('experiment', experiment);
  }
}

export function getExperimentVariableFromCookie() {
  return Cookies.get('oviva_experiment');
}

export function setOvivaCookie(cookieName: string, cookieValue: string) {
  const isSet = Cookies.get(cookieName);
  if (isSet) {
    return;
  }

  const expiresAt = new Date();
  expiresAt.setMinutes(expiresAt.getMinutes() + 30);

  const ovivaCookieApi = Cookies.withAttributes({
    path: '/',
    domain: '.' + DOMAIN,
    expires: expiresAt,
    secure: true,
  });
  ovivaCookieApi.set(cookieName, cookieValue);
}

export function setTrackingCookiesFromUrlParams() {
  if (typeof window === 'undefined') {
    return;
  }

  const searchParams = new URLSearchParams(window.location.search);

  const getParam = (key: string): string => {
    return searchParams.get(key) || "";
  };

  setOvivaCookie("oviva_http_referrer", document.referrer.substring(0, 254));
  setOvivaCookie("oviva_source", getParam("utm_source"));
  setOvivaCookie("oviva_medium", getParam("utm_medium"));
  setOvivaCookie("oviva_campaign", getParam("utm_campaign"));
  setOvivaCookie("oviva_term", getParam("utm_term"));
  setOvivaCookie("oviva_content", getParam("utm_content"));
  setOvivaCookie("oviva_gclid", getParam("gclid"));
  setOvivaCookie("oviva_ad_id", getParam("utm_id"));
  setOvivaCookie("oviva_landing_page", document.location.href);
}
